import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService, { type LoginResponse, type DecodedToken } from '@/services/auth'
import apiService from '@/services/api'
import router from '@/router'

/**
 * A subscription package as the API returns it. `price_ron` is an INTEGER
 * number of RON per month, VAT included — never a decimal string, because the
 * price is a round figure agreed offline, not a computed amount.
 */
export interface SubscriptionPackage {
  id: string
  code: string
  name: string
  description?: string | null
  price_ron: number
  tier: number
  is_default: boolean
  is_active: boolean
  features: string[]
}

/** A scheduled downgrade: it takes effect on the 1st of the next month. */
export interface PendingSubscriptionChange {
  package: SubscriptionPackage
  /** `YYYY-MM-01`, Europe/Bucharest. */
  effective_on: string
}

/** What one client holds right now, plus anything scheduled. */
export interface ClientSubscription {
  package: SubscriptionPackage | null
  /** ISO timestamp the current package started at. */
  since: string | null
  pending: PendingSubscriptionChange | null
  /** Effective feature keys — everything when enforcement is off, or for staff. */
  features: string[]
}

export interface Client {
  id: string
  firm_id: string
  user_id: string
  societate?: string
  cui?: string
  registrul_comertului?: string
  data_inregistrarii?: string
  impozit?: string
  platitor_tva?: boolean
  reprezentant?: string
  phone?: string
  email?: string
  address?: string
  created_at?: string
  updated_at?: string
  invoicing_enabled?: boolean
  /** Only on `GET /clients/:id` — the list endpoint deliberately omits it. */
  subscription?: ClientSubscription
}

export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'accountant' | 'user'
  firm_id?: string
  locale: string
  client?: Client // For users with role='user', contains client data
  // Optional fields from Client model (for users with role='user')
  phone?: string
  societate?: string
  cui?: string
  registrul_comertului?: string
  email_notifications_enabled?: boolean
  push_notifications_enabled?: boolean
  /** Whether the financial statistics module is enabled for this user's firm. */
  stats_enabled?: boolean
  /** Whether this exact client is part of the invoice pilot. */
  invoicing_enabled?: boolean
  /** Whether this client has retained invoices that must remain readable. */
  invoice_archive_available?: boolean
  /**
   * Global switch. While it is false the packages exist but gate nothing, so
   * the whole subscription UI stays invisible.
   */
  subscriptions_enforced?: boolean
  /** Present for role='user' only (login, refresh and `/users/me`). */
  subscription?: ClientSubscription
  /**
   * The invoicing module is enabled for the firm but this client's package does
   * not include it — the page renders its locked state instead of 403-ing.
   */
  invoicing_locked?: boolean
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(authService.getToken())
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value && !authService.isTokenExpired())
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isAccountant = computed(
    () => user.value?.role === 'accountant' || user.value?.role === 'admin'
  )
  const isUser = computed(() => user.value?.role === 'user')
  const userRole = computed(() => user.value?.role)
  const firmId = computed(() => user.value?.firm_id)
  /**
   * Whether package gating is switched on platform-wide. Every subscription
   * affordance hangs off this: with it false the feature ships invisibly.
   */
  const subscriptionsEnforced = computed(() => user.value?.subscriptions_enforced === true)

  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = (await apiService.login(email, password)) as LoginResponse

      authService.setToken(response.token)
      token.value = response.token
      user.value = response.user

      await router.push({ name: 'dashboard' })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    loading.value = true

    try {
      await apiService.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      authService.removeToken()
      token.value = null
      user.value = null
      loading.value = false
      await router.push({ name: 'login' })
    }
  }

  async function loadUser(): Promise<void> {
    if (!token.value || authService.isTokenExpired()) {
      user.value = null
      return
    }

    try {
      const userData = await apiService.getMe()
      user.value = userData
    } catch (err: unknown) {
      console.error('Failed to load user:', err)

      // Only log out on authentication errors (401), not network errors
      // Check if it's an axios error with a 401 response
      const axiosError = err as { response?: { status?: number } }
      if (axiosError?.response?.status === 401) {
        authService.removeToken()
        token.value = null
        user.value = null
        await router.push({ name: 'login' })
      }
      // For network errors, keep the token and user state unchanged
      // This allows the app to work offline/when server is temporarily down
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      await apiService.changePassword(currentPassword, newPassword)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Password change failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  function hasRole(requiredRole: 'admin' | 'accountant' | 'user'): boolean {
    if (!user.value) return false

    const roleHierarchy: Record<string, number> = {
      admin: 3,
      accountant: 2,
      user: 1
    }

    return roleHierarchy[user.value.role] >= roleHierarchy[requiredRole]
  }

  function hasAnyRole(roles: Array<'admin' | 'accountant' | 'user'>): boolean {
    return roles.some((role) => hasRole(role))
  }

  function checkTokenExpiry(): void {
    if (token.value && authService.isTokenExpired()) {
      logout()
    }
  }

  async function initialize(): Promise<void> {
    const storedToken = authService.getToken()
    if (storedToken && !authService.isTokenExpired()) {
      token.value = storedToken
      await loadUser()
    } else {
      authService.removeToken()
      token.value = null
      user.value = null
    }
  }

  async function fetchUser(): Promise<void> {
    if (!isAuthenticated.value) return

    loading.value = true
    try {
      const userData = await apiService.getMe()
      user.value = userData
    } catch (err) {
      console.error('Failed to fetch user:', err)
      error.value = err instanceof Error ? err.message : 'Failed to fetch user'
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isAccountant,
    isUser,
    userRole,
    firmId,
    subscriptionsEnforced,
    login,
    logout,
    loadUser,
    fetchUser,
    changePassword,
    hasRole,
    hasAnyRole,
    checkTokenExpiry,
    initialize
  }
})
